/**
 * 移动端库存管理页
 */
import React, { useEffect, useState } from 'react';
import {
  List,
  PullToRefresh,
  Empty,
  Skeleton,
  SearchBar,
  Tag,
  Selector,
  Button,
  Popup,
  Stepper,
} from 'antd-mobile';
import { adminInventoryApi } from '../../api/index';
import Toast from 'antd-mobile/es/components/toast';

export default function MobileInventoryPage() {
  const [loading, setLoading] = useState(true);
  const [inventory, setInventory] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [keyword, setKeyword] = useState('');
  const [searchKeyword, setSearchKeyword] = useState('');

  // 筛选条件
  const [stockStatus, setStockStatus] = useState('');
  const [sortBy, setSortBy] = useState('quantity_desc');

  // 编辑弹窗
  const [editVisible, setEditVisible] = useState(false);
  const [editVariant, setEditVariant] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [saving, setSaving] = useState(false);

  // 批量编辑模式
  const [batchMode, setBatchMode] = useState(false);
  const [batchValues, setBatchValues] = useState({});
  const [batchSaving, setBatchSaving] = useState(false);

  // 历史记录弹窗
  const [historyVisible, setHistoryVisible] = useState(false);
  const [historyVariant, setHistoryVariant] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyLoading, setHistoryLoading] = useState(false);

  useEffect(() => {
    fetchInventory(1);
  }, [searchKeyword, stockStatus, sortBy]);

  const fetchInventory = async (pageNum) => {
    try {
      setLoading(true);
      const params = {
        page: pageNum,
        pageSize: 15,
        keyword: searchKeyword || undefined,
        stockStatus: stockStatus || undefined,
        sortBy: sortBy || undefined,
      };
      const data = await adminInventoryApi.list(params);
      setInventory(data?.items || []);
      setTotal(data?.total || 0);
      setPage(pageNum);
    } catch (err) {
      Toast.show('获取库存数据失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePrevPage = () => {
    if (page > 1) fetchInventory(page - 1);
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(total / 15);
    if (page < totalPages) fetchInventory(page + 1);
  };

  const refreshData = async () => {
    await fetchInventory(1);
  };

  const handleSearch = () => {
    setSearchKeyword(keyword);
  };

  // 库存状态标签颜色
  const getStockTag = (qty) => {
    if (qty === null || qty === undefined) {
      return <Tag color="default">未设置</Tag>;
    }
    if (qty <= 0) {
      return <Tag color="danger">缺货</Tag>;
    }
    if (qty <= 10) {
      return <Tag color="warning">低库存</Tag>;
    }
    return <Tag color="success">充足</Tag>;
  };

  // 打开编辑弹窗
  const openEdit = (item) => {
    setEditVariant(item);
    setEditValue(String(item.inventoryQuantity || 0));
    setEditVisible(true);
  };

  // 保存库存
  const saveInventory = async () => {
    const newQty = parseInt(editValue, 10);
    if (isNaN(newQty) || newQty < 0) {
      Toast.show('请输入有效数量');
      return;
    }
    setSaving(true);
    try {
      await adminInventoryApi.update(editVariant.id, newQty);
      Toast.show('库存已更新');
      // 更新本地数据
      setInventory(inventory.map(item =>
        item.id === editVariant.id ? { ...item, inventoryQuantity: newQty } : item
      ));
      setEditVisible(false);
    } catch (err) {
      Toast.show(err?.response?.data?.message || '保存失败');
    } finally {
      setSaving(false);
    }
  };

  // 进入批量模式：以当前库存初始化
  const enterBatchMode = () => {
    const init = {};
    inventory.forEach((it) => { init[it.id] = it.inventoryQuantity ?? 0; });
    setBatchValues(init);
    setBatchMode(true);
  };

  // 退出批量模式
  const exitBatchMode = () => {
    setBatchMode(false);
    setBatchValues({});
  };

  // 有改动的行
  const getBatchChangedItems = () => {
    return inventory
      .filter((it) => batchValues[it.id] !== undefined
        && batchValues[it.id] !== null
        && batchValues[it.id] !== (it.inventoryQuantity ?? 0))
      .map((it) => ({ variantId: it.id, quantity: batchValues[it.id] }));
  };

  // 保存全部
  const saveBatch = async () => {
    const items = getBatchChangedItems();
    if (items.length === 0) {
      Toast.show('没有需要保存的改动');
      return;
    }
    setBatchSaving(true);
    try {
      await adminInventoryApi.batchUpdate(items);
      Toast.show(`已批量更新 ${items.length} 项`);
      const changedMap = {};
      items.forEach((i) => { changedMap[i.variantId] = i.quantity; });
      // 函数式更新，避免陈旧闭包读到旧的 inventory 快照
      setInventory((prev) => prev.map((it) =>
        changedMap[it.id] !== undefined ? { ...it, inventoryQuantity: changedMap[it.id] } : it
      ));
      exitBatchMode();
    } catch (err) {
      Toast.show(err?.response?.data?.message || '批量保存失败');
    } finally {
      setBatchSaving(false);
    }
  };

  // 打开历史记录弹窗
  const openHistory = (item) => {
    setHistoryVariant(item);
    setHistoryVisible(true);
    setHistoryPage(1);
    fetchHistory(item.id, 1);
  };

  // 获取历史记录
  const fetchHistory = async (variantId, pageNum) => {
    setHistoryLoading(true);
    try {
      const data = await adminInventoryApi.getHistory(variantId, { page: pageNum, pageSize: 10 });
      setHistoryData(data?.items || data?.list || []);
      setHistoryTotal(data?.total || 0);
      setHistoryPage(pageNum);
    } catch (err) {
      Toast.show('获取历史记录失败');
      setHistoryData([]);
      setHistoryTotal(0);
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleHistoryPrevPage = () => {
    if (historyPage > 1) fetchHistory(historyVariant.id, historyPage - 1);
  };

  const handleHistoryNextPage = () => {
    const totalPages = Math.ceil(historyTotal / 10);
    if (historyPage < totalPages) fetchHistory(historyVariant.id, historyPage + 1);
  };

  // 关闭历史弹窗
  const closeHistory = () => {
    setHistoryVisible(false);
    setHistoryVariant(null);
    setHistoryData([]);
    setHistoryTotal(0);
  };

  // 类型映射
  const getTypeLabel = (type) => {
    const typeConfig = {
      manual: { label: '手动调整', color: 'primary' },
      order_adjustment: { label: '订单调整', color: 'warning' },
      refund: { label: '退款入库', color: 'success' },
      transfer: { label: '调拨', color: 'default' },
      other: { label: '其他', color: 'default' },
    };
    return typeConfig[type] || { label: type || '-', color: 'default' };
  };

  // 批量模式下增大底部内边距，防止固定栏遮挡列表末尾
  return (
    <div style={{ padding: '12px', paddingBottom: batchMode ? 80 : 60 }}>
      {/* 批量编辑入口 */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8 }}>
        {!batchMode ? (
          <Button size="small" color="primary" fill="outline" onClick={enterBatchMode}>
            批量编辑
          </Button>
        ) : (
          <span style={{ fontSize: 13, color: '#1677ff' }}>批量编辑中</span>
        )}
      </div>
      {/* 批量模式下隐藏搜索栏和筛选区，防止筛选条件变更触发 fetchInventory 导致 batchValues 与列表错位 */}
      {!batchMode && (
        <>
          {/* 搜索栏 */}
          <SearchBar
            placeholder="搜索商品、SKU"
            value={keyword}
            onChange={setKeyword}
            onSearch={handleSearch}
            onClear={() => setSearchKeyword('')}
            style={{ marginBottom: 12 }}
          />

          {/* 筛选选项 */}
          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>库存状态</div>
            <Selector
              options={[
                { label: '全部', value: '' },
                { label: '低库存', value: 'low' },
                { label: '缺货', value: 'out' },
                { label: '充足', value: 'sufficient' },
              ]}
              value={[stockStatus]}
              onChange={(v) => setStockStatus(v[0] || '')}
              style={{ '--gap': '8px' }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>排序</div>
            <Selector
              options={[
                { label: '库存升序', value: 'quantity_asc' },
                { label: '库存降序', value: 'quantity_desc' },
                { label: '名称 A-Z', value: 'product_asc' },
                { label: '名称 Z-A', value: 'product_desc' },
              ]}
              value={[sortBy]}
              onChange={(v) => setSortBy(v[0] || 'quantity_asc')}
              style={{ '--gap': '8px' }}
            />
          </div>
        </>
      )}

      {/* 统计卡片 */}
      <div style={{
        display: 'flex',
        gap: 12,
        marginBottom: 12,
        background: '#f5f5f5',
        borderRadius: 8,
        padding: 12,
      }}>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#52c41a' }}>充足</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#52c41a' }}>
            {inventory.filter(v => v.inventoryQuantity > 10).length}
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#fa8c16' }}>低库存</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#fa8c16' }}>
            {inventory.filter(v => v.inventoryQuantity > 0 && v.inventoryQuantity <= 10).length}
          </div>
        </div>
        <div style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ fontSize: 12, color: '#f5222d' }}>缺货</div>
          <div style={{ fontSize: 18, fontWeight: 600, color: '#f5222d' }}>
            {inventory.filter(v => v.inventoryQuantity <= 0).length}
          </div>
        </div>
      </div>

      {/* 列表 */}
      <PullToRefresh onRefresh={refreshData}>
        {loading && page === 1 ? (
          <Skeleton.Paragraph lineCount={5} />
        ) : inventory.length === 0 ? (
          <Empty description="暂无库存数据" />
        ) : (
          <>
            <List>
              {inventory.map((item) => (
                <List.Item
                  key={item.id}
                  onClick={() => { if (!batchMode) openEdit(item); }}
                  arrow={
                    batchMode ? (
                      <Stepper
                        min={0}
                        digits={0}
                        value={batchValues[item.id] ?? 0}
                        // 清空时 antd-mobile 可能传 null，归一为 0 与 PC 端 InputNumber 行为一致
                        onChange={(v) => setBatchValues((prev) => ({ ...prev, [item.id]: v ?? 0 }))}
                        style={{ '--input-width': '48px' }}
                      />
                    ) : (
                      <div style={{ display: 'flex', gap: 8 }}>
                        <Button
                          size="small"
                          fill="outline"
                          onClick={(e) => { e.stopPropagation(); openHistory(item); }}
                        >
                          历史
                        </Button>
                        <Button
                          size="small"
                          fill="outline"
                          onClick={(e) => { e.stopPropagation(); openEdit(item); }}
                        >
                          编辑
                        </Button>
                      </div>
                    )
                  }
                >
                  <div style={{ fontSize: 14 }}>
                    <div style={{ fontWeight: 500, marginBottom: 4 }}>{item.productTitle || '-'}</div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                      <span style={{ color: '#666', fontSize: 12 }}>SKU: {item.sku || '-'}</span>
                      <span style={{ color: '#999', fontSize: 12 }}>|</span>
                      <span style={{ color: '#666', fontSize: 12 }}>{item.variantTitle || '默认'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      {getStockTag(item.inventoryQuantity)}
                      <span style={{ fontWeight: 600, fontSize: 16 }}>库存: {item.inventoryQuantity ?? '-'}</span>
                      <span style={{ color: '#52c41a', fontSize: 13 }}>${Number(item.price || 0).toFixed(2)}</span>
                    </div>
                  </div>
                </List.Item>
              ))}
            </List>
            {/* 分页控件 */}
            <div style={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 16,
              padding: '16px 0',
            }}>
              {/* 批量模式下禁用翻页，防止跨页数据污染 */}
              <Button
                size="small"
                fill="outline"
                disabled={page <= 1 || batchMode}
                onClick={handlePrevPage}
                style={{ width: 80 }}
              >
                上一页
              </Button>
              <span style={{ fontSize: 14, color: '#666' }}>
                {page} / {Math.max(1, Math.ceil(total / 15))}（共 {total} 条）
              </span>
              <Button
                size="small"
                fill="outline"
                disabled={page >= Math.ceil(total / 15) || batchMode}
                onClick={handleNextPage}
                style={{ width: 80 }}
              >
                下一页
              </Button>
            </div>
          </>
        )}
      </PullToRefresh>

      {/* 编辑库存弹窗 */}
      <Popup
        visible={editVisible}
        onMaskClick={() => setEditVisible(false)}
        bodyStyle={{ padding: 20, minHeight: '40vh' }}
      >
        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 16, fontWeight: 500, marginBottom: 8 }}>编辑库存</div>
          <div style={{ fontSize: 13, color: '#666' }}>
            <div>商品: {editVariant?.productTitle}</div>
            <div>SKU: {editVariant?.sku}</div>
            <div>变体: {editVariant?.variantTitle || '默认'}</div>
          </div>
        </div>

        <div style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 13, color: '#666', marginBottom: 8 }}>库存数量</div>
          <input
            type="number"
            value={editValue}
            onChange={(e) => setEditValue(e.target.value)}
            style={{
              width: '100%',
              padding: '12px',
              fontSize: 16,
              border: '1px solid #ddd',
              borderRadius: 8,
            }}
            min="0"
          />
        </div>

        <div style={{ display: 'flex', gap: 12 }}>
          <Button
            block
            fill="outline"
            onClick={() => setEditVisible(false)}
          >
            取消
          </Button>
          <Button
            block
            color="primary"
            loading={saving}
            onClick={saveInventory}
          >
            保存
          </Button>
        </div>
      </Popup>

      {/* 库存变化历史弹窗 */}
      <Popup
        visible={historyVisible}
        onMaskClick={closeHistory}
        bodyStyle={{ height: '70vh', overflow: 'auto' }}
      >
        {/* 头部 */}
        <div style={{
          padding: 16,
          background: '#f5f5f5',
          borderBottom: '1px solid #eee',
          position: 'sticky',
          top: 0,
          zIndex: 1,
        }}>
          <div style={{ fontSize: 16, fontWeight: 600, marginBottom: 8 }}>
            库存变化历史
          </div>
          {historyVariant && (
            <div style={{ fontSize: 13, color: '#666', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <span>{historyVariant.productTitle}</span>
              <span style={{ fontFamily: 'monospace' }}>SKU: {historyVariant.sku}</span>
              <span>当前: <strong style={{ color: '#1677ff' }}>{historyVariant.inventoryQuantity ?? '-'}</strong></span>
            </div>
          )}
        </div>

        {/* 历史列表 */}
        <div style={{ padding: 12 }}>
          {historyLoading && historyData.length === 0 ? (
            <Skeleton.Paragraph lineCount={5} />
          ) : historyData.length === 0 ? (
            <Empty description="暂无历史记录" style={{ padding: 40 }} />
          ) : (
            <>
              {historyData.map((record, idx) => {
                const change = record.quantityChange || 0;
                const changeColor = change > 0 ? '#52c41a' : change < 0 ? '#f5222d' : '#666';
                const typeConfig = getTypeLabel(record.changeType);

                return (
                  <div
                    key={record.id || idx}
                    style={{
                      background: '#fff',
                      borderRadius: 8,
                      padding: 12,
                      marginBottom: 8,
                      boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
                      <span style={{ fontSize: 12, color: '#999' }}>{record.createdAt}</span>
                      <Tag color={typeConfig.color} fill="outline">{typeConfig.label}</Tag>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                      <span style={{ fontSize: 14, color: '#999' }}>{record.quantityBefore ?? '-'}</span>
                      <span style={{ fontSize: 16, fontWeight: 600, color: changeColor }}>
                        {change > 0 ? `+${change}` : change}
                      </span>
                      <span style={{ fontSize: 14 }}>=</span>
                      <span style={{ fontSize: 16, fontWeight: 600 }}>{record.quantityAfter ?? '-'}</span>
                    </div>
                    <div style={{ fontSize: 12, color: '#666' }}>
                      {record.reason || '-'} · {record.operatorName || '系统'}
                    </div>
                  </div>
                );
              })}
              {historyTotal > 10 && (
                <div style={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  gap: 16,
                  marginTop: 8,
                }}>
                  <Button
                    size="small"
                    fill="outline"
                    disabled={historyPage <= 1}
                    loading={historyLoading}
                    onClick={handleHistoryPrevPage}
                    style={{ width: 80 }}
                  >
                    上一页
                  </Button>
                  <span style={{ fontSize: 14, color: '#666' }}>
                    {historyPage} / {Math.max(1, Math.ceil(historyTotal / 10))}（共 {historyTotal} 条）
                  </span>
                  <Button
                    size="small"
                    fill="outline"
                    disabled={historyPage >= Math.ceil(historyTotal / 10)}
                    loading={historyLoading}
                    onClick={handleHistoryNextPage}
                    style={{ width: 80 }}
                  >
                    下一页
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </Popup>

      {/* 批量保存底部固定栏 */}
      {batchMode && (
        <div style={{
          position: 'fixed',
          left: 0,
          right: 0,
          bottom: 0,
          display: 'flex',
          gap: 12,
          padding: 12,
          background: '#fff',
          borderTop: '1px solid #eee',
          zIndex: 100,
        }}>
          <Button block fill="outline" onClick={exitBatchMode}>取消</Button>
          <Button
            block
            color="primary"
            loading={batchSaving}
            onClick={saveBatch}
          >
            保存全部({getBatchChangedItems().length})
          </Button>
        </div>
      )}
    </div>
  );
}
